import React, { useMemo, useState } from "react";
import { useWarningZoneContext } from "../../../contexts/WarningZoneContext";
import { useSidebarContext } from "../../../contexts/SidebarContext";
import { useVesselContext } from "../../../contexts/VesselContext";
import { useAppContext } from "../../../contexts/AppContext";
import { useChatContext } from "../../../contexts/ChatContext";
import { sendMessage } from "../../../hooks/useChatMessages";
import searchStyles from "./SeacrhVessel.module.css";
import chatStyles from "./ChatMessage.module.css";
import Btn from "../../Btn/Btn";
import LeftArrowIcon from "../../Icons/LeftArrowIcon";
import PaperPlaneIcon from "../../Icons/PaperPlaneIcon";
import L from "leaflet";
import { mapBounds } from "../../../constants/Map";
import styles from "./WarningZoneVesselList.module.css"
import { useMapDisplayContext } from "../../../contexts/MapDisplayContext";
import { sanitizeInput, removeDiacritics } from "../../../utils/textInput";
const WarningZoneVesselList: React.FC = () => {
    const { warningZones } = useWarningZoneContext();
    const { vessels } = useVesselContext();
    const { chosenWarningZoneId, setContentType, setChosenWarningZoneId } = useSidebarContext();
    const { showDialog } = useAppContext();
    const { setWarningZoneMessage, warningZoneMessage, setChats, chats } = useChatContext();
    const [checked, setChecked] = useState<Record<string, boolean>>({});
    const [sending, setSending] = useState(false);
    const [sentUnitIds, setSentUnitIds] = useState<string[]>([]);
    const { action } = useMapDisplayContext();

    const zone = warningZones[chosenWarningZoneId];
    const vesselsInZone = useMemo(
        () =>
            Object.values(vessels).filter(
                v => {
                    const center = L.latLng(zone.centerLat, zone.centerLon);
                    const lastValidPosition = [...v.positions].reverse().find((pos: any) => mapBounds.contains([pos.latitude, pos.longitude])) ?? v.positions.at(-1);
                    const markerLatLng = L.latLng(lastValidPosition?.latitude, lastValidPosition?.longitude);
                    const distance = center.distanceTo(markerLatLng); // đơn vị: mét
                    const radius = zone.radius;    // đơn vị: mét
                    return distance <= radius;
                }
            ),
        [vessels, chosenWarningZoneId]
    );
    const allChecked = vesselsInZone.length > 0 && vesselsInZone.every(v => checked[v.unitId]);

    const handleCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checkedAll = e.target.checked;
        const newChecked: Record<string, boolean> = {};
        vesselsInZone.forEach(v => { newChecked[v.unitId] = checkedAll; });
        setChecked(newChecked);
    };
    const handleCheck = (unitId: string, checkedVal: boolean) => {
        setChecked(prev => ({ ...prev, [unitId]: checkedVal }));
    };

    const checkedUnitIds = vesselsInZone.filter(v => checked[v.unitId]).map(v => v.unitId);

    const defaultMsg = zone ? `Cảnh báo vùng: ${zone.name}\nNội dung: ${zone.content}\nCảnh báo từ ${new Date(zone.startTime).toLocaleString()} đến ${new Date(zone.endTime).toLocaleString()}` : "";
    const [message, setMessage] = useState(defaultMsg);

    const handleSend = async () => {
        if (message.trim() == "" || !zone || checkedUnitIds.length == 0) return;
        const confirm = await showDialog("Xác nhận", "Bạn có chắc chắn muốn gửi tin nhắn này cho các tàu đã chọn?", "confirm");
        if (!confirm) return;
        setSending(true);
        const payload = removeDiacritics(message);
        const results = await Promise.all(
            //   checkedUnitIds.map(unitId => sendMessage(unitId, payload))
            checkedUnitIds.map(unitId => true)
        );
        setSending(false);
        const failed: string[] = [];
        const sent: string[] = [];
        results.forEach((res, idx) => {
            //   if (!res || res.status !== 200) failed.push(checkedUnitIds[idx]);
            if (!res) failed.push(checkedUnitIds[idx]);
            else sent.push(checkedUnitIds[idx]);
        });
        setSentUnitIds(sent);
        if (failed.length > 0) {
            showDialog("Thông báo", `Không gửi được tin nhắn tới các tàu: ${failed.join(", ")}`, "error");
        } else {
            showDialog("Thành công", "Đã gửi tin nhắn tới tất cả tàu đã chọn!", "success");
        }
        if (sent.length > 0) {
            const sentTime = Date.now();
            const newMessages = { ...warningZoneMessage };
            newMessages[zone.id] = newMessages[zone.id] ?? [];
            newMessages[zone.id].push({
                zoneId: zone.id,
                message: payload,
                unitIds: sent,
                sentTime
            });
            setWarningZoneMessage(newMessages);
            // Thêm tin nhắn vào chats cho từng unitId đã gửi; nếu chưa có chat thì khởi tạo mới
            const nextChats = { ...chats };
            sent.forEach(unitId => {
                if (!nextChats[unitId]) {
                    // Khởi tạo chat mới nếu chưa tồn tại
                    nextChats[unitId] = {
                        lastReadTime: 0,
                        messages: [],
                        contactName: vessels[unitId]?.vesselName ?? "#",
                        contactNumber: vessels[unitId]?.contactNumber ?? unitId,
                        lastInBoundMessageIndex: -1,
                        lastRealMessageIndex: -1,
                        read: true,
                    };
                }
                // Thêm tin nhắn vào cuối danh sách messages
                nextChats[unitId].messages.push({ message: payload, direction: "out", messagetime: sentTime, read: true, timestampcontent: null });
            });
            setChats(nextChats);
        }
    };

    if (!zone) return null;

    return (
        <>
            <div className={"w-100 pe-2 ps-2 pt-2 pb-0"}>
                <div className="d-flex align-items-center mb-3">
                    <Btn className={styles["left-btn"] + " text"} title="Quay lại" onClick={() => { setChosenWarningZoneId(""); setContentType("warningZoneList"); }}>
                        <LeftArrowIcon />
                    </Btn>
                    <div className="flex-grow-1 text-center fw-bold">{zone.name}</div>
                </div>
                <div className="d-flex align-items-center">
                    {vesselsInZone.length == 0 ? "" : <><input type="checkbox" checked={allChecked} id="check-all" onChange={handleCheckAll} />
                        <span className="ms-2">Chọn tất cả</span></>}
                </div>
            </div>
            <div className={searchStyles["search-results-list"] + " styled-scrollbar p-1 pt-0"}>
                {vesselsInZone.length == 0 ? <div className="text-center text-muted">Không có tàu trong vùng cảnh báo</div> : vesselsInZone.map(vessel => (
                    <div className={styles.item} key={vessel.unitId}>
                        <input
                            type="checkbox"
                            checked={!!checked[vessel.unitId]}
                            onChange={e => handleCheck(vessel.unitId, e.target.checked)}
                            style={{ marginRight: 8 }}
                        />
                        <div className={styles.itemInfo} onClick={() => {
                            const lastValidPosition = [...vessel.positions].reverse().find((pos: any) => mapBounds.contains([pos.latitude, pos.longitude]));
                            let validLat = lastValidPosition?.latitude;
                            let validLon = lastValidPosition?.longitude;
                            action.setView(validLat, validLon, 12);
                            action.clickVesselMarker(vessel.unitId);
                        }}>
                            <div className={searchStyles["name"]}>{vessel.vesselName}</div>
                            <div className={searchStyles["unitid"]}>{vessel.unitId}</div>
                        </div>
                    </div>
                ))}
            </div>
            {checkedUnitIds.length > 0 && (
                <div className={chatStyles["chat-input"]}>
                    <textarea
                        className={chatStyles["chat-input-textarea"] + " styled-scrollbar"}
                        value={message}
                        onChange={e => setMessage(sanitizeInput(e.target.value || '').slice(0, 160))}
                        onPaste={e => {
                            e.preventDefault();
                            const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                            const sanitized = sanitizeInput(paste || '');
                            setMessage(prev => (prev + sanitized).slice(0, 160));
                        }}
                        disabled={sending}
                        maxLength={160}
                    />
                    <Btn className={"text"} title="Gửi" onClick={handleSend} disabled={sending}>
                        <PaperPlaneIcon />
                    </Btn>
                </div>
            )}
        </>
    );
};

export default WarningZoneVesselList;
